import React from 'react';
import { string, func, bool } from 'prop-types';
import classNames from 'classnames';
import { useConfiguration } from '../../context/configurationContext';
import { intlShape, injectIntl } from '../../util/reactIntl';
import { lazyLoadWithDimensions } from '../../util/uiHelpers';
import { propTypes } from '../../util/types';
import { formatMoney } from '../../util/currency';
import { ensureListing, ensureUser } from '../../util/data';
import { richText } from '../../util/richText';
import { createSlug } from '../../util/urlHelpers';
import moment from 'moment';
import { AspectRatioWrapper, NamedLink, ResponsiveImage } from '../../components';
import css from './ListingCard.module.css';
import { isArrayLength } from '../../util/genericHelpers';
import { getAuctionExpires, getListingPricingType, getUserDetails } from '../../util/dataExtractor';
import { FieldConfigKeys, listingPricing } from '../../util/enums';

const MIN_LENGTH_FOR_LONG_WORDS = 10;

const priceData = (price, currency, intl) => {
  if (price && price.currency === currency) {
    const formattedPrice = formatMoney(intl, price);
    return { formattedPrice, priceTitle: formattedPrice };
  } else if (price) {
    return {
      formattedPrice: intl.formatMessage(
        { id: 'ListingCard.unsupportedPrice' },
        { currency: price.currency }
      ),
      priceTitle: intl.formatMessage(
        { id: 'ListingCard.unsupportedPriceTitle' },
        { currency: price.currency }
      ),
    };
  }
  return {};
};

const LazyImage = lazyLoadWithDimensions(ResponsiveImage, { loadAfterInitialRendering: 3000 });

const PriceMaybe = ({ price, publicData, config, intl, listingPricingType }) => {
  const { formattedPrice, priceTitle } = priceData(price, config.currency, intl);
  const priceMessages = {
    [listingPricing.FIXED_PRICE]: intl.formatMessage(
      {
        id: 'ListingCard.fixedPriceBid',
      },
      { formattedPrice }
    ),
    [listingPricing.ONE_WEEK_AUCTION]: intl.formatMessage(
      {
        id: 'ListingCard.oneWeekAuctionBid',
      },
      { formattedPrice }
    ),
    [listingPricing.BLIND_AUCTION]: intl.formatMessage({
      id: 'ListingCard.blindBid',
    }),
  };
  const priceMessage = priceMessages[listingPricingType] || '';

  return (
    <>
      <div className={css.price}>
        <div className={css.priceValue} />
      </div>
      <div className={css.price}>
        <div className={css.priceValue} title={priceTitle}>
          {priceMessage}
        </div>
      </div>
    </>
  );
};

export const ListingCardComponent = props => {
  const config = useConfiguration();
  const { className, rootClassName, intl, listing, renderSizes, setActiveListing } = props;
  const classes = classNames(rootClassName || css.root, className);
  const currentListing = ensureListing(listing);
  const id = currentListing.id.uuid;
  const { title = '', price, publicData } = currentListing.attributes || {};
  const slug = createSlug(title);
  const author = ensureUser(listing.author);
  const { organizationName } = !!author?.id && getUserDetails(author);
  const firstImage =
    currentListing.images && currentListing.images.length > 0 ? currentListing.images[0] : null;

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const variants = firstImage
    ? Object.keys(firstImage?.attributes?.variants).filter(k => k.startsWith(variantPrefix))
    : [];

  const setActivePropsMaybe = setActiveListing
    ? {
        onMouseEnter: () => setActiveListing(currentListing.id),
        onMouseLeave: () => setActiveListing(null),
      }
    : null;

  const gradingStatusOptions = config.listing.listingFields.find(
    f => f.key === FieldConfigKeys.GRADING_STATUS
  )?.enumOptions;
  const inventoryPreppedOptions = config.listing.listingFields.find(
    f => f.key === FieldConfigKeys.INVENTORY_PREPPED
  )?.enumOptions;
  const itemSourceOptions = config.listing.listingFields.find(
    f => f.key === FieldConfigKeys.ITEM_SOURCE
  )?.enumOptions;

  const gradingStatusLabel =
    isArrayLength(gradingStatusOptions) &&
    gradingStatusOptions.find(o => o.option === publicData?.grading_status)?.label;
  const inventoryPreppedLabel =
    isArrayLength(inventoryPreppedOptions) &&
    inventoryPreppedOptions.find(o => o.option === publicData?.inventory_prepped)?.label;
    
  const { item_source } = publicData || {};

  const listingPricingType = getListingPricingType(currentListing);
  const auctionExpires = getAuctionExpires(currentListing);
  const currentDate = moment();

  const auctionExpiresDate =
    auctionExpires && currentDate.isBefore(moment.unix(auctionExpires))
      ? moment.unix(auctionExpires).format('MM/DD/YYYY')
      : null;

  const { priceTitle } = priceData(price, config.currency, intl) || {};
  const { BLIND_AUCTION, ONE_WEEK_AUCTION } = listingPricing;

  const itemSourceLabels = isArrayLength(item_source)
    ? item_source.map(source => {
        const matchedOption = itemSourceOptions.find(option => option.option === source);
        return matchedOption ? matchedOption.label : source;
      })
    : [];
  return (
    <NamedLink className={classes} name="ListingPage" params={{ id, slug }}>
      <AspectRatioWrapper
        className={css.aspectRatioWrapper}
        width={aspectWidth}
        height={aspectHeight}
        {...setActivePropsMaybe}
      >
        <LazyImage
          rootClassName={css.rootForImage}
          alt={title}
          image={firstImage}
          variants={variants}
          sizes={renderSizes}
        />
      </AspectRatioWrapper>
      <div className={css.info}>
        <div className={css.infoTop}>
          <div className={css.title}>
            {richText(title, {
              longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
              longWordClass: css.longWord,
            })}
          </div>

          <p className={css.organizationName}>{organizationName}</p>
          <ul>
            <li>{gradingStatusLabel}</li>
            <li>{inventoryPreppedLabel}</li>
          </ul>
          <ul>{itemSourceLabels?.map((label, i) => <li key={i}>{label}</li>)}</ul>
        </div>
        <div className={css.userDetails}>
          <div className={css.userinfo}>
            <div className={css.userinfoLeft}>
              {auctionExpiresDate && (
                <div className={css.auctionExpires}>
                  {intl.formatMessage({
                    id:
                      listingPricingType === ONE_WEEK_AUCTION
                        ? 'ListingCard.auctionExpires'
                        : 'ListingCard.blindAuctionExpires',
                  })}
                </div>
              )}
              <PriceMaybe
                price={price}
                publicData={publicData}
                config={config}
                intl={intl}
                listingPricingType={listingPricingType}
              />
            </div>
          </div>
        </div>
        <div className={classNames(css.userDetails, css.timingOptions)}>
          <div className={css.userinfo}>
            <div className={css.userinfoLeft}>
              {(listingPricingType === ONE_WEEK_AUCTION || listingPricingType === BLIND_AUCTION) &&
              auctionExpiresDate ? (
                <div className={css.auctionExpires}>
                  {moment(auctionExpiresDate).format('MM/DD')}
                </div>
              ) : null}

              <div />
              {priceTitle}
            </div>
          </div>
        </div>
      </div>
    </NamedLink>
  );
};

ListingCardComponent.defaultProps = {
  className: null,
  rootClassName: null,
  renderSizes: null,
  setActiveListing: null,
  showAuthorInfo: true,
};

ListingCardComponent.propTypes = {
  className: string,
  rootClassName: string,
  intl: intlShape.isRequired,
  listing: propTypes.listing.isRequired,
  showAuthorInfo: bool,

  // Responsive image sizes hint
  renderSizes: string,

  setActiveListing: func,
};

export default injectIntl(ListingCardComponent);
